import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Snackbar,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Chip,
  InputAdornment,
  TextField,
  TablePagination,
  Tooltip,
} from '@mui/material';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import DescriptionIcon from '@mui/icons-material/Description';
import SecurityIcon from '@mui/icons-material/Security';
import SearchIcon from '@mui/icons-material/Search';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import LockOpenOutlinedIcon from '@mui/icons-material/LockOpenOutlined';
import Layout from '../components/Layout';
import { API_BASE_URL } from '../apiBase';

export default function UserPermissions() {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();

  const initialUser = location.state?.user || null;

  const [user, setUser] = useState(initialUser);
  const [loading, setLoading] = useState(!initialUser);
  const [saving, setSaving] = useState(false);
  const [pageOptions, setPageOptions] = useState([]);
  const [permValues, setPermValues] = useState({});
  const [toastOpen, setToastOpen] = useState(false);
  const [toastSeverity, setToastSeverity] = useState('success');
  const [toastMessage, setToastMessage] = useState('');
  const [expandedKey, setExpandedKey] = useState(null);

  // User list mode state
  const [usersList, setUsersList] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersSearch, setUsersSearch] = useState('');
  const [usersPage, setUsersPage] = useState(0);
  const [usersRowsPerPage, setUsersRowsPerPage] = useState(10);

  const showToast = (severity, message) => {
    setToastSeverity(severity);
    setToastMessage(message);
    setToastOpen(true);
  };

  const userId = Number(id);

  useEffect(() => {
    if (userId) return;
    const fetchUsers = async () => {
      setUsersLoading(true);
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_BASE_URL}/api/users`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (res.ok && Array.isArray(data.users)) {
          setUsersList(data.users);
        }
      } catch (e) {
        showToast('error', 'Failed to load users');
      } finally {
        setUsersLoading(false);
      }
    };
    fetchUsers();
  }, [userId]);

  const loadPages = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/api/pages`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Failed to load pages');
      }
      const items = Array.isArray(data.pages) ? data.pages : [];
      const mapped = items.map((p) => ({
        key: p.page_key,
        label: p.label,
        route: p.route,
      }));
      setPageOptions(mapped);
    } catch (e) {
      showToast('error', e.message || 'Failed to load pages list');
      setPageOptions([]);
    }
  };

  const loadPermissions = async (uid) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/api/users/${uid}/permissions`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        return;
      }
      const data = await res.json();
      const items = Array.isArray(data.page_permissions) ? data.page_permissions : [];
      const next = {};
      items.forEach((item) => {
        const key = String(item?.page_key || '').trim();
        if (!key) return;
        const can_create = Boolean(item.can_create);
        const can_update = Boolean(item.can_update);
        const can_delete = Boolean(item.can_delete);
        const can_import = Boolean(item.can_import);
        const can_export = Boolean(item.can_export);
        next[key] = {
          can_create,
          can_update,
          can_delete,
          can_import,
          can_export,
          can_view: !can_create && !can_update && !can_delete && !can_import && !can_export,
        };
      });
      setPermValues(next);
    } catch {
      setPermValues({});
    }
  };

  useEffect(() => {
    if (!userId) return;

    const load = async () => {
      setLoading(true);
      try {
        if (!initialUser) {
          const token = localStorage.getItem('token');
          const res = await fetch(`${API_BASE_URL}/api/users`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          const data = await res.json();
          if (res.ok && Array.isArray(data.users)) {
            const found = data.users.find((u) => Number(u.id) === userId);
            if (found) {
              setUser(found);
            }
          }
        }
        await loadPermissions(userId);
        await loadPages();
      } catch (e) {
        showToast('error', e.message || 'Failed to load permissions');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [userId]);

  const togglePage = (key) => {
    setPermValues((prev) => {
      const next = { ...prev };
      if (next[key]) {
        delete next[key];
        return next;
      }
      next[key] = {
        can_create: true,
        can_update: true,
        can_delete: true,
        can_view: false,
      };
      return next;
    });
  };

  const selectedCount = useMemo(() => Object.keys(permValues).length, [permValues]);
  const totalPages = pageOptions.length;
  const allSelected = selectedCount === totalPages && totalPages > 0;
  const someSelected = selectedCount > 0 && selectedCount < totalPages;

  const toggleAll = () => {
    setPermValues((prev) => {
      if (Object.keys(prev).length === totalPages) {
        return {};
      }
      const next = {};
      pageOptions.forEach((p) => {
        next[p.key] = {
          can_create: true,
          can_update: true,
          can_delete: true,
          can_view: false,
        };
      });
      return next;
    });
  };

  const toggleCrud = (key, field) => {
    setPermValues((prev) => {
      const existing = prev[key] || {
        can_create: false,
        can_update: false,
        can_delete: false,
        can_import: false,
        can_export: false,
        can_view: false,
      };
      const next = { ...existing };
      if (field === 'insert') {
        next.can_create = !next.can_create;
      } else if (field === 'update') {
        next.can_update = !next.can_update;
      } else if (field === 'delete') {
        next.can_delete = !next.can_delete;
      } else if (field === 'import') {
        next.can_import = !next.can_import;
      } else if (field === 'export') {
        next.can_export = !next.can_export;
      }
      if (
        !next.can_create &&
        !next.can_update &&
        !next.can_delete &&
        !next.can_import &&
        !next.can_export &&
        !next.can_view
      ) {
        const { [key]: _removed, ...rest } = prev;
        return rest;
      }
      return {
        ...prev,
        [key]: next,
      };
    });
  };

  const toggleView = (key) => {
    setPermValues((prev) => {
      const existing = prev[key];
      if (!existing) {
        return {
          ...prev,
          [key]: {
            can_create: false,
            can_update: false,
            can_delete: false,
            can_import: false,
            can_export: false,
            can_view: true,
          },
        };
      }
      const next = {
        ...existing,
        can_view: !existing.can_view,
      };
      const hasCrud =
        next.can_create || next.can_update || next.can_delete || next.can_import || next.can_export;
      if (!hasCrud && !next.can_view) {
        const { [key]: _removed, ...rest } = prev;
        return rest;
      }
      return {
        ...prev,
        [key]: next,
      };
    });
  };

  const selectedSummary = (perm, hasEntry) => {
    if (!hasEntry) return 'No permissions selected';
    const labels = [];
    if (perm.can_create) labels.push('Insert');
    if (perm.can_update) labels.push('Update');
    if (perm.can_delete) labels.push('Delete');
    if (perm.can_import) labels.push('Import');
    if (perm.can_export) labels.push('Export');
    if (!labels.length) labels.push('View');
    return labels.join(', ');
  };

  const handleSave = async () => {
    if (!userId) return;
    try {
      setSaving(true);
      const payload = Object.entries(permValues).map(([pageKey, perms]) => ({
        page_key: pageKey,
        can_create: perms.can_create,
        can_update: perms.can_update,
        can_delete: perms.can_delete,
        can_import: perms.can_import,
        can_export: perms.can_export,
      }));
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/api/users/${userId}/permissions`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ permissions: payload }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to update permissions');
      showToast('success', 'Permissions updated');
    } catch (e) {
      showToast('error', e.message || 'Failed to update permissions');
    } finally {
      setSaving(false);
    }
  };

  const titleEmail = user?.email || '';
  const roleLabel = user?.role || '';

  const handleToggleRestriction = async (u) => {
    if (!window.confirm(`Are you sure you want to ${u.is_restricted ? 'unrestrict' : 'restrict'} this account?`)) {
      return;
    }
    try {
      const token = localStorage.getItem('token');
      // Toggle the status
      const newStatus = !u.is_restricted;
      const res = await fetch(`${API_BASE_URL}/api/users/${u.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ 
          full_name: u.full_name,
          email: u.email,
          role: u.role,
          is_restricted: newStatus 
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Failed to update restriction status');
      }
      
      // Update local state
      setUsersList((prev) =>
        prev.map((user) =>
          user.id === u.id ? { ...user, is_restricted: newStatus ? 1 : 0 } : user
        )
      );
      
      showToast('success', `User account ${newStatus ? 'restricted' : 'unrestricted'}`);
    } catch (e) {
      showToast('error', e.message || 'Failed to update restriction status');
    }
  };

  if (!userId) {
    const filteredUsers = usersList.filter((u) => {
      const q = usersSearch.toLowerCase();
      return (
        (u.full_name || '').toLowerCase().includes(q) ||
        (u.email || '').toLowerCase().includes(q) ||
        (u.role || '').toLowerCase().includes(q)
      );
    });

    const count = filteredUsers.length;
    const paginated = filteredUsers.slice(
      usersPage * usersRowsPerPage,
      usersPage * usersRowsPerPage + usersRowsPerPage
    );

    return (
      <Layout>
        <Box sx={{ p: 3 }}>
          <Typography variant="h5" sx={{ mb: 3 }}>
            User Permissions
          </Typography>

          <Paper sx={{ width: '100%', mb: 2, p: 2 }}>
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <TextField
                placeholder="Search users..."
                size="small"
                value={usersSearch}
                onChange={(e) => {
                  setUsersSearch(e.target.value);
                  setUsersPage(0);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{ width: 300 }}
              />
            </Box>

            <TableContainer>
              <Table size="medium">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {usersLoading ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                        <CircularProgress size={30} />
                      </TableCell>
                    </TableRow>
                  ) : count === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                        No users found
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginated.map((u) => (
                      <TableRow key={u.id} hover>
                        <TableCell>{u.full_name || '—'}</TableCell>
                        <TableCell>{u.email}</TableCell>
                        <TableCell>
                          <Chip
                            label={u.role || 'user'}
                            size="small"
                            color={u.role === 'superadmin' ? 'secondary' : 'default'}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title={u.is_restricted ? 'Unrestrict Account' : 'Restrict Account'}>
                            <IconButton
                              size="small"
                              color={u.is_restricted ? 'error' : 'success'}
                              onClick={() => handleToggleRestriction(u)}
                              sx={{ mr: 1 }}
                            >
                              {u.is_restricted ? <LockOutlinedIcon /> : <LockOpenOutlinedIcon />}
                            </IconButton>
                          </Tooltip>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<SecurityIcon />}
                            onClick={() => navigate(`/user-permissions/${u.id}`, { state: { user: u } })}
                          >
                            Manage Permissions
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={count}
              rowsPerPage={usersRowsPerPage}
              page={usersPage}
              onPageChange={(e, p) => setUsersPage(p)}
              onRowsPerPageChange={(e) => {
                setUsersRowsPerPage(parseInt(e.target.value, 10));
                setUsersPage(0);
              }}
            />
          </Paper>
        </Box>
        <Snackbar
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          open={toastOpen}
          autoHideDuration={2500}
          onClose={() => setToastOpen(false)}
        >
          <Alert onClose={() => setToastOpen(false)} severity={toastSeverity} sx={{ width: '100%' }}>
            {toastMessage}
          </Alert>
        </Snackbar>
      </Layout>
    );
  }

  return (
    <Layout>
      <Box
        sx={{
          width: '100%',
          mt: 2,
          px: { xs: 1, sm: 2 },
        }}
      >
        <Paper
          sx={{
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: '0 18px 45px rgba(15,23,42,0.08)',
            p: 2.5,
          }}
        >
          <Box sx={{ mb: 2 }}>
            <Typography sx={{ fontWeight: 500, fontSize: 16 }}>
              {titleEmail || `User #${userId}`}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Role: <b>{roleLabel || '—'}</b>
            </Typography>
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          indeterminate={someSelected}
                          checked={allSelected}
                          onChange={toggleAll}
                        />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 500 }}>Page Name</TableCell>
                      <TableCell sx={{ fontWeight: 500 }}>Route</TableCell>
                      <TableCell sx={{ fontWeight: 500 }} align="right">
                        Status
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pageOptions.map((p) => {
                      const allowed = Boolean(permValues[p.key]);
                      const perm = permValues[p.key] || {
                        can_create: false,
                        can_update: false,
                        can_delete: false,
                        can_import: false,
                        can_export: false,
                        can_view: false,
                      };
                      const expanded = expandedKey === p.key;
                      const hasCrud =
                        perm.can_create ||
                        perm.can_update ||
                        perm.can_delete ||
                        perm.can_import ||
                        perm.can_export;
                      const viewChecked =
                        Boolean(perm.can_view) || (allowed && !hasCrud);
                      const insertSelected = perm.can_create;
                      const updateSelected = perm.can_update;
                      const deleteSelected = perm.can_delete;
                      const importSelected = perm.can_import;
                      const exportSelected = perm.can_export;

                      return (
                        <React.Fragment key={p.key}>
                          <TableRow hover>
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={allowed}
                                onChange={() => togglePage(p.key)}
                              />
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    setExpandedKey(expanded ? null : p.key)
                                  }
                                >
                                  <KeyboardArrowRightIcon
                                    fontSize="small"
                                    sx={{
                                      color: 'text.secondary',
                                      transform: expanded ? 'rotate(90deg)' : 'none',
                                      transition: 'transform 0.2s',
                                    }}
                                  />
                                </IconButton>
                                <DescriptionIcon
                                  fontSize="small"
                                  sx={{ color: 'text.secondary' }}
                                />
                                <Typography sx={{ fontWeight: 500 }}>{p.label}</Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                {p.route}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Chip
                                label={allowed ? 'Allowed' : 'Restricted'}
                                size="small"
                                color={allowed ? 'success' : 'default'}
                                variant={allowed ? 'filled' : 'outlined'}
                                onClick={() => togglePage(p.key)}
                                sx={{ fontWeight: 500 }}
                              />
                            </TableCell>
                          </TableRow>
                          {expanded && (
                            <TableRow>
                              <TableCell colSpan={4} sx={{ bgcolor: 'action.hover' }}>
                                <Box
                                  sx={{
                                    mt: 1,
                                    mb: 1,
                                    p: 2,
                                    borderRadius: 2,
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      mb: 1.5,
                                      gap: 1,
                                    }}
                                  >
                                    <SecurityIcon
                                      sx={{ fontSize: 18, color: 'text.secondary' }}
                                    />
                                    <Typography variant="body2">
                                      Set Permissions for{' '}
                                      <b>{titleEmail || 'User'}</b> on this page:
                                    </Typography>
                                  </Box>
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      flexWrap: 'wrap',
                                      gap: 1.5,
                                      mb: 1,
                                    }}
                                  >
                                    <Button
                                      variant="outlined"
                                      color="primary"
                                      size="small"
                                      onClick={() => toggleCrud(p.key, 'insert')}
                                      sx={{
                                        borderRadius: 3,
                                        textTransform: 'none',
                                        borderWidth: 1.5,
                                        borderColor: insertSelected
                                          ? 'primary.main'
                                          : 'divider',
                                        color: insertSelected
                                          ? 'primary.main'
                                          : 'text.secondary',
                                        backgroundColor: 'transparent',
                                        backgroundImage: 'none',
                                        boxShadow: 'none',
                                      }}
                                    >
                                      <Checkbox
                                        size="small"
                                        checked={perm.can_create}
                                        sx={{ p: 0.5, mr: 0.5 }}
                                      />
                                      Insert
                                    </Button>
                                    <Button
                                      variant="outlined"
                                      color="primary"
                                      size="small"
                                      onClick={() => toggleCrud(p.key, 'update')}
                                      sx={{
                                        borderRadius: 3,
                                        textTransform: 'none',
                                        borderWidth: 1.5,
                                        borderColor: updateSelected
                                          ? 'primary.main'
                                          : 'divider',
                                        color: updateSelected
                                          ? 'primary.main'
                                          : 'text.secondary',
                                        backgroundColor: 'transparent',
                                        backgroundImage: 'none',
                                        boxShadow: 'none',
                                      }}
                                    >
                                      <Checkbox
                                        size="small"
                                        checked={perm.can_update}
                                        sx={{ p: 0.5, mr: 0.5 }}
                                      />
                                      Update
                                    </Button>
                                    <Button
                                      variant="outlined"
                                      color="primary"
                                      size="small"
                                      onClick={() => toggleCrud(p.key, 'delete')}
                                      sx={{
                                        borderRadius: 3,
                                        textTransform: 'none',
                                        borderWidth: 1.5,
                                        borderColor: deleteSelected
                                          ? 'primary.main'
                                          : 'divider',
                                        color: deleteSelected
                                          ? 'primary.main'
                                          : 'text.secondary',
                                        backgroundColor: 'transparent',
                                        backgroundImage: 'none',
                                        boxShadow: 'none',
                                      }}
                                    >
                                      <Checkbox
                                        size="small"
                                        checked={perm.can_delete}
                                        sx={{ p: 0.5, mr: 0.5 }}
                                      />
                                      Delete
                                    </Button>
                                    <Button
                                      variant="outlined"
                                      color="primary"
                                      size="small"
                                      onClick={() => toggleCrud(p.key, 'import')}
                                      sx={{
                                        borderRadius: 3,
                                        textTransform: 'none',
                                        borderWidth: 1.5,
                                        borderColor: importSelected
                                          ? 'primary.main'
                                          : 'divider',
                                        color: importSelected
                                          ? 'primary.main'
                                          : 'text.secondary',
                                        backgroundColor: 'transparent',
                                        backgroundImage: 'none',
                                        boxShadow: 'none',
                                      }}
                                    >
                                      <Checkbox
                                        size="small"
                                        checked={perm.can_import}
                                        sx={{ p: 0.5, mr: 0.5 }}
                                      />
                                      Import
                                    </Button>
                                    <Button
                                      variant="outlined"
                                      color="primary"
                                      size="small"
                                      onClick={() => toggleCrud(p.key, 'export')}
                                      sx={{
                                        borderRadius: 3,
                                        textTransform: 'none',
                                        borderWidth: 1.5,
                                        borderColor: exportSelected
                                          ? 'primary.main'
                                          : 'divider',
                                        color: exportSelected
                                          ? 'primary.main'
                                          : 'text.secondary',
                                        backgroundColor: 'transparent',
                                        backgroundImage: 'none',
                                        boxShadow: 'none',
                                      }}
                                    >
                                      <Checkbox
                                        size="small"
                                        checked={perm.can_export}
                                        sx={{ p: 0.5, mr: 0.5 }}
                                      />
                                      Export
                                    </Button>
                                    <Button
                                      variant="outlined"
                                      color="primary"
                                      size="small"
                                      onClick={() => toggleView(p.key)}
                                      sx={{
                                        borderRadius: 3,
                                        textTransform: 'none',
                                        borderWidth: 1.5,
                                        borderColor: viewChecked
                                          ? 'primary.main'
                                          : 'divider',
                                        color: viewChecked
                                          ? 'primary.main'
                                          : 'text.secondary',
                                        backgroundColor: 'transparent',
                                        backgroundImage: 'none',
                                        boxShadow: 'none',
                                      }}
                                    >
                                      <Checkbox
                                        size="small"
                                        checked={viewChecked}
                                        sx={{ p: 0.5, mr: 0.5 }}
                                      />
                                      View
                                    </Button>
                                  </Box>
                                  <Typography
                                    variant="caption"
                                    sx={{ color: 'text.secondary' }}
                                  >
                                    Selected:
                                    <Box
                                      component="span"
                                      sx={{ fontStyle: 'italic', ml: 0.25 }}
                                    >
                                      {selectedSummary(perm, allowed)}
                                    </Box>
                                  </Typography>
                                </Box>
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3, gap: 1.5 }}>
                <Button variant="outlined" onClick={() => navigate('/users')} disabled={saving}>
                  Back
                </Button>
                <Button variant="contained" onClick={handleSave} disabled={saving}>
                  Save
                </Button>
              </Box>
            </>
          )}
        </Paper>

        <Snackbar
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          open={toastOpen}
          autoHideDuration={2500}
          onClose={() => setToastOpen(false)}
        >
          <Alert onClose={() => setToastOpen(false)} severity={toastSeverity} sx={{ width: '100%' }}>
            {toastMessage}
          </Alert>
        </Snackbar>
      </Box>
    </Layout>
  );
}
